import { useReactiveVar } from '@apollo/client'
import {
  EditorInput,
  EditorInputState,
  LinkButton,
  Switch,
  Text,
  TextHighlight,
  TextSize,
  TextWeight
} from '@focaldata/cin-ui-components'
import { Box, TextField } from '@mui/material'
import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import {
  DraftConjointItem,
  EntryType,
  ResponseOptionMedia,
  ResponseOptionMediaInput
} from '../../../data/gql-gen/questionnaire/graphql'
import {
  MediaType,
  QuestionSettingCode,
  QuestionValidationError
} from '../../../data/model/questionnaire'
import useDraftQuestionnaireIdCache from '../../../hooks/localState/useDraftQuestionnaireIdCache'
import { useDuplicateQuestionnaireEntry } from '../../../hooks/questionnaire/useDuplicateQuestionnaireEntry'
import useGetDraftQuestionnaire from '../../../hooks/questionnaire/useGetDraftQuestionnaire'
import useQuestionnaireValidation from '../../../hooks/questionnaire/useQuestionnaireValidation'
import useSetQuestionnaireSetting from '../../../hooks/questionnaire/useSetQuestionnaireSetting'
import { useUpdateConjoint } from '../../../hooks/questionnaire/useUpdateConjoint'
import BasicQuestionLayout from '../../../layouts/BasicQuestionLayout'
import { isFocaldataUser } from '../../../utils/authUtils'
import { WithEntryOfType, newEntryId } from '../../../utils/questionnaireUtils'
import { getRawValueFromJsonString } from '../CardTitle/CardTitle.utils'
import DefaultOptions from '../QuestionCardDefaultOptions/DefaultOptions'
import { flattenEntries } from '../Questionnaire.utils'
import { SurveyQuestionCardHeaderContainer } from '../SurveyQuestionCardHeader'
import { ConjointAttributesList } from './ConjointAttributesList'
import { ConjointSettings } from './ConjointSettings'
import { PackageRestrictionsDialogProvider } from './PackageRestrictions/PackageRestrictionsDialogProvider'
import PackageRestrictionsTrigger from './PackageRestrictions/PackageRestrictionsTrigger'

interface Props extends WithEntryOfType<EntryType.ConjointEntryType> {
  isNewlyCreated?: boolean
}

export interface ConjointAttribute {
  id: string
  name: string
  isAppearance: boolean
  values: ConjointAttributeValue[]
}

export interface ConjointAttributeValue {
  id: string
  value: string
  media: ResponseOptionMediaInput | null
}

// Strips __typename (and any other output-only fields) from a queried media
// object — sending it back verbatim as a mutation variable would fail GraphQL
// input validation.
const toConjointOptionMediaInput = (
  media: ResponseOptionMedia | null | undefined
): ResponseOptionMediaInput | null =>
  media
    ? {
        mediaId: media.mediaId,
        mediaName: media.mediaName,
        mediaType: media.mediaType,
        mediaUrl: media.mediaUrl,
        renderedMediaUrl: media.renderedMediaUrl
      }
    : null

// Shared by the initial useState and the entry-resync effect below. Keeps
// isAppearance/media in local state (unlike allowedPackages — see
// PackageRestrictionsDialogProvider) wrapped exactly like the GQL input, so
// every handler below can resend an untouched option's stored image just by
// spreading it, without a separate mapping step.
const buildConjointState = (entryItem: DraftConjointItem) => ({
  text: entryItem.question?.text || '',
  textStyling: entryItem.question?.textStyling,
  attributes: entryItem.attributes.map((attr) => ({
    attributeId: attr.attributeId,
    name: attr.name,
    position: attr.position,
    isAppearance: attr.isAppearance ?? false,
    options: attr.options.map((option) => ({
      attributeOptionId: option.attributeOptionId,
      position: option.position,
      value: option.value,
      media: { value: toConjointOptionMediaInput(option.media) }
    }))
  }))
})

export const ConjointEntryContext = createContext<
  DraftConjointItem | undefined
>(undefined)

export const useConjointEntryContext = () => {
  const entryItem = useContext(ConjointEntryContext)

  if (!entryItem) {
    throw new Error('Missing ConjointEntryContext.Provider')
  }

  return entryItem
}

const ConjointQuestion: React.FC<Props> = (props: Props) => {
  const { entry, isNewlyCreated } = props

  const entryItem = entry.entryItem

  // Using local state to handle real-time updates while debouncing the API call
  const [conjointState, setConjointState] = useState(() =>
    buildConjointState(entry.entryItem)
  )

  const questionnaireId = useDraftQuestionnaireIdCache()

  const panelSize = parseInt(
    entry.entryItem.settingValues.find(
      (setting) => setting.code === QuestionSettingCode.ConjointPanelSize
    )?.value || '2',
    10
  )
  const numberOfPanels = parseInt(
    entry.entryItem.settingValues.find(
      (setting) => setting.code === QuestionSettingCode.ConjointNumberOfPanels
    )?.value || '5',
    10
  )
  const packageLabelsEnabled =
    entry.entryItem.settingValues.find(
      (setting) =>
        setting.code === QuestionSettingCode.ConjointPackageLabelsEnabled
    )?.value === 'enabled'
  const savedPackageLabels: string[] = (() => {
    try {
      const raw = entry.entryItem.settingValues.find(
        (setting) => setting.code === QuestionSettingCode.ConjointPackageLabels
      )?.value
      if (!raw) return []
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  })()
  const [packageLabels, setPackageLabels] =
    useState<string[]>(savedPackageLabels)

  const {
    updateConjointDebounced,
    updateConjoint,
    cancelPendingUpdate,
    loading: savingConjoint
  } = useUpdateConjoint({ questionId: entry.id })

  const { duplicateQuestionnaireEntry } = useDuplicateQuestionnaireEntry(
    entry.id
  )

  const { handleSettingChange } = useSetQuestionnaireSetting(entry)
  const { getQuestionValidationErrors } = useQuestionnaireValidation()

  const validationErrors = getQuestionValidationErrors(entry.id)
  const hasPackageLabelErrors = validationErrors?.errors?.includes(
    QuestionValidationError.EmptyPackageLabelError
  )
  const hasPackageRestrictionErrors = validationErrors?.errors?.includes(
    QuestionValidationError.PackageMissingEligibleValueError
  )
  const hasMissingAppearanceImageErrors = validationErrors?.errors?.includes(
    QuestionValidationError.MissingAppearanceImageError
  )

  const { draftQuestionnaireEntries } = useGetDraftQuestionnaire()
  const flatDraftQuestionnaireEntries = flattenEntries(
    draftQuestionnaireEntries
  )

  // Transform local state to component interface for UI components
  const attributes: ConjointAttribute[] = conjointState.attributes.map(
    (attr) => ({
      id: attr.attributeId,
      name: attr.name,
      isAppearance: attr.isAppearance,
      values: attr.options.map((option) => ({
        id: option.attributeOptionId,
        value: option.value,
        media: option.media.value
      }))
    })
  )

  const updateQuestion = (inputState: EditorInputState) => {
    const question = inputState.toRaw()
    const newTextStyling = JSON.stringify(question)
    if (newTextStyling !== entry.entryItem.question?.textStyling) {
      setConjointState((prev) => {
        const newState = {
          ...prev,
          text: inputState.toText(),
          textStyling: newTextStyling
        }

        updateConjointDebounced(newState)

        return newState
      })
    }
  }

  const duplicateQuestion = useCallback(async () => {
    await duplicateQuestionnaireEntry({
      position: entry.position + 1
    })
  }, [duplicateQuestionnaireEntry, entry.position])

  const updateAttribute = (attributeId: string, name: string) => {
    setConjointState((prev) => {
      const newState = {
        ...prev,
        attributes: prev.attributes.map((attr) =>
          attr.attributeId === attributeId ? { ...attr, name } : attr
        )
      }

      updateConjointDebounced(newState)

      return newState
    })
  }

  const addAttribute = useCallback(() => {
    const newPosition = conjointState.attributes.length
    cancelPendingUpdate()

    const newAttribute = {
      name: '',
      position: newPosition,
      options: [
        {
          position: 0,
          value: ''
        },
        {
          position: 1,
          value: ''
        }
      ]
    }

    updateConjoint({
      text: conjointState.text,
      textStyling: conjointState.textStyling,
      attributes: [...conjointState.attributes, newAttribute]
    })
  }, [conjointState, updateConjoint, cancelPendingUpdate])

  const removeAttribute = (attributeId: string) => {
    // Prevent removing attributes if it would result in less than 2 attributes
    if (conjointState.attributes.length <= 2) {
      return
    }

    cancelPendingUpdate()
    updateConjoint({
      text: conjointState.text,
      textStyling: conjointState.textStyling,
      attributes: conjointState.attributes
        .filter((attr) => attr.attributeId !== attributeId)
        .map((attr, index) => ({
          ...attr,
          position: index
        }))
    })
  }

  const addAttributeValue = (attributeId: string) => {
    const targetAttribute = conjointState.attributes.find(
      (attr) => attr.attributeId === attributeId
    )
    if (!targetAttribute) return

    cancelPendingUpdate()

    const newOption = {
      position: targetAttribute.options.length,
      value: ''
    }

    updateConjoint({
      text: conjointState.text,
      textStyling: conjointState.textStyling,
      attributes: conjointState.attributes.map((attr) =>
        attr.attributeId === attributeId
          ? {
              ...attr,
              options: [...attr.options, newOption]
            }
          : attr
      )
    })
  }

  const updateAttributeValue = (
    attributeId: string,
    valueId: string,
    value: string
  ) => {
    setConjointState((prev) => {
      const newState = {
        ...prev,
        attributes: prev.attributes.map((attr) =>
          attr.attributeId === attributeId
            ? {
                ...attr,
                options: attr.options.map((option) =>
                  option.attributeOptionId === valueId
                    ? { ...option, value }
                    : option
                )
              }
            : attr
        )
      }

      updateConjointDebounced(newState)

      return newState
    })
  }

  const removeAttributeValue = (attributeId: string, valueId: string) => {
    // Find the target attribute and prevent removal if it would result in less than 2 options
    const targetAttribute = conjointState.attributes.find(
      (attr) => attr.attributeId === attributeId
    )
    if (!targetAttribute || targetAttribute.options.length <= 2) {
      return
    }

    cancelPendingUpdate()
    updateConjoint({
      text: conjointState.text,
      textStyling: conjointState.textStyling,
      attributes: conjointState.attributes.map((attr) =>
        attr.attributeId === attributeId
          ? {
              ...attr,
              options: attr.options
                .filter((option) => option.attributeOptionId !== valueId)
                .map((option, index) => ({
                  ...option,
                  position: index
                }))
            }
          : attr
      )
    })
  }

  // Only one attribute can be the appearance attribute, so turning it on for
  // one forces it off on every other.
  const toggleAppearanceAttribute = (attributeId: string) => {
    cancelPendingUpdate()
    setConjointState((prev) => {
      const newState = {
        ...prev,
        attributes: prev.attributes.map((attr) => ({
          ...attr,
          isAppearance:
            attr.attributeId === attributeId ? !attr.isAppearance : false
        }))
      }

      updateConjoint(newState)

      return newState
    })
  }

  // Unlike the debounced text handlers above, these are one-shot actions
  // whose caller (RowMediaUploader) awaits the result to show its own
  // success/error state — so, unlike the fire-and-forget updateConjoint
  // calls above, this one is awaited and a failed save is surfaced by
  // rejecting, rather than leaving the uploaded file looking saved when it
  // never reached the server.
  const uploadAttributeValueMedia = async (
    attributeId: string,
    optionId: string,
    mediaUrl: string,
    mediaName: string,
    mediaType: MediaType,
    renderedMediaUrl: string
  ) => {
    cancelPendingUpdate()
    const newState = {
      ...conjointState,
      attributes: conjointState.attributes.map((attr) =>
        attr.attributeId === attributeId
          ? {
              ...attr,
              options: attr.options.map((option) =>
                option.attributeOptionId === optionId
                  ? {
                      ...option,
                      media: {
                        value: {
                          mediaUrl,
                          mediaName,
                          mediaType,
                          renderedMediaUrl
                        }
                      }
                    }
                  : option
              )
            }
          : attr
      )
    }
    setConjointState(newState)

    const result = await updateConjoint(newState)
    // useUpdateConjoint's onError makes Apollo resolve rather than reject on
    // failure, so a failed save must be detected from the result, not caught.
    if (result.errors?.length) {
      throw new Error('Failed to save image')
    }
  }

  const removeAttributeValueMedia = async (
    attributeId: string,
    optionId: string
  ) => {
    cancelPendingUpdate()
    const newState = {
      ...conjointState,
      attributes: conjointState.attributes.map((attr) =>
        attr.attributeId === attributeId
          ? {
              ...attr,
              options: attr.options.map((option) =>
                option.attributeOptionId === optionId
                  ? { ...option, media: { value: null } }
                  : option
              )
            }
          : attr
      )
    }
    setConjointState(newState)

    const result = await updateConjoint(newState)
    if (result.errors?.length) {
      throw new Error('Failed to remove image')
    }
  }

  const updatePanelSize = (size: number) => {
    handleSettingChange([
      {
        settingCode: QuestionSettingCode.ConjointPanelSize,
        settingValue: size.toString()
      }
    ])
  }

  const updateNumberOfPanels = (count: number) => {
    handleSettingChange([
      {
        settingCode: QuestionSettingCode.ConjointNumberOfPanels,
        settingValue: count.toString()
      }
    ])
  }

  const updatePackageLabelsEnabled = (enabled: boolean) => {
    const emptyLabels = Array(panelSize).fill('')
    setPackageLabels(enabled ? emptyLabels : [])
    handleSettingChange([
      {
        settingCode: QuestionSettingCode.ConjointPackageLabelsEnabled,
        settingValue: enabled ? 'enabled' : 'disabled'
      },
      {
        settingCode: QuestionSettingCode.ConjointPackageLabels,
        settingValue: JSON.stringify(enabled ? emptyLabels : [])
      }
    ])
  }

  const updatePackageLabel = (index: number, label: string) => {
    setPackageLabels((prev) => {
      const newLabels = [...prev]
      while (newLabels.length <= index) {
        newLabels.push('')
      }
      newLabels[index] = label
      return newLabels
    })
  }

  const persistPackageLabels = () => {
    const padded = Array.from(
      { length: panelSize },
      (_, i) => packageLabels[i] || ''
    )
    handleSettingChange([
      {
        settingCode: QuestionSettingCode.ConjointPackageLabels,
        settingValue: JSON.stringify(padded)
      }
    ])
  }

  useEffect(() => {
    setPackageLabels(savedPackageLabels)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(savedPackageLabels)])

  useEffect(() => {
    setConjointState(buildConjointState(entry.entryItem))
  }, [entry])

  const newlyCreatedEntryId = useReactiveVar(newEntryId)
  const isNewlyCreatedEntry =
    !!newlyCreatedEntryId && newlyCreatedEntryId === entry.id

  return (
    <ConjointEntryContext.Provider value={entryItem}>
      <BasicQuestionLayout
        entryId={entry.id}
        questionCardHeader={
          <SurveyQuestionCardHeaderContainer
            entry={entry}
            hasError={false}
            titleContent={
              <EditorInput
                placeholder="Type your question"
                rawValue={getRawValueFromJsonString(
                  conjointState.textStyling ?? '',
                  conjointState.text
                )}
                onChange={updateQuestion}
                isError={
                  !isNewlyCreatedEntry &&
                  validationErrors?.errors?.includes(
                    QuestionValidationError.EmptyQuestionTextError
                  )
                }
                helperText="Please add a question title"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={isNewlyCreated}
              />
            }
            onClickDuplicateIcon={duplicateQuestion}
            isMultipleChoice={false}
            disabledDuplicate={false}
            entries={flatDraftQuestionnaireEntries}
          />
        }
        responseOptions={
          <PackageRestrictionsDialogProvider
            entryAttributes={entry.entryItem.attributes}
            conjointState={conjointState}
            panelSize={panelSize}
            updateConjoint={updateConjoint}
            cancelPendingUpdate={cancelPendingUpdate}
            saving={savingConjoint}
          >
            <ConjointAttributesList
              attributes={attributes}
              entryId={entry.id}
              questionnaireId={questionnaireId}
              hasMissingAppearanceImageErrors={hasMissingAppearanceImageErrors}
              onUpdateAttribute={updateAttribute}
              onRemoveAttribute={removeAttribute}
              onAddAttributeValue={addAttributeValue}
              onUpdateAttributeValue={updateAttributeValue}
              onRemoveAttributeValue={removeAttributeValue}
              onToggleAppearance={toggleAppearanceAttribute}
              onUploadAttributeValueMedia={uploadAttributeValueMedia}
              onRemoveAttributeValueMedia={removeAttributeValueMedia}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 2,
                py: 1,
                width: '100%'
              }}
            >
              <LinkButton
                highlight={TextHighlight.Emphasis}
                size={TextSize.m}
                weight={TextWeight.SemiBold}
                onClick={addAttribute}
                noPadding
              >
                Add Attribute
              </LinkButton>
              <ConjointSettings
                panelSize={panelSize}
                numberOfPanels={numberOfPanels}
                onPanelSizeChange={updatePanelSize}
                onNumberOfPanelsChange={updateNumberOfPanels}
              />
            </Box>
            {isFocaldataUser() && (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  width: '100%',
                  '& .MuiFormControlLabel-root': { ml: 0 }
                }}
              >
                <Switch
                  label="Package labels"
                  ariaLabel="Toggle package labels"
                  checked={packageLabelsEnabled}
                  onChange={(e) => updatePackageLabelsEnabled(e.target.checked)}
                />
                {packageLabelsEnabled && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      mt: 1.5
                    }}
                  >
                    {Array.from({ length: panelSize }, (_, i) => {
                      const hasLabelError =
                        hasPackageLabelErrors &&
                        !(packageLabels[i] || '').trim()
                      return (
                        <TextField
                          key={i}
                          size="small"
                          label={`Package ${i + 1}`}
                          value={packageLabels[i] || ''}
                          onChange={(e) =>
                            updatePackageLabel(i, e.target.value)
                          }
                          onBlur={persistPackageLabels}
                          error={hasLabelError}
                          helperText={
                            hasLabelError
                              ? 'Package label is required'
                              : undefined
                          }
                          sx={{ maxWidth: 300 }}
                        />
                      )
                    })}
                  </Box>
                )}
                <Box sx={{ mt: 1.5 }}>
                  <PackageRestrictionsTrigger />
                </Box>
              </Box>
            )}
            {hasPackageRestrictionErrors && (
              <Box sx={{ px: 2, py: 1, width: '100%' }}>
                <Text size={TextSize.ms} highlight={TextHighlight.Error}>
                  {isFocaldataUser()
                    ? 'Every package needs at least one eligible value for every attribute. Open Package restrictions to fix it.'
                    : 'Every package needs at least one eligible value for every attribute. Ask a Focaldata teammate to fix the package restrictions on this question.'}
                </Text>
              </Box>
            )}
          </PackageRestrictionsDialogProvider>
        }
        defaultOptions={
          <DefaultOptions
            entry={entry}
            options={[
              { code: QuestionSettingCode.NoneOfThese, label: 'None of these' }
            ]}
          />
        }
      />
    </ConjointEntryContext.Provider>
  )
}

export default memo(ConjointQuestion)
